import { RefObject, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Canvas as THiveCanvas } from '@thive/canvas';

import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { logger } from '@common/logging/logger';
import { getUserPreference, UserPreferenceName } from '@/modules/userPreferences';
import { useUserPreferencesStore } from '@/modules/userPreferences/store';

import { useCanvasStore } from '../store';

export const useCanvasSetup = (elementRef: RefObject<HTMLDivElement | null>) => {
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);

  const { initialize, dispose } = useCanvasStore(
    useShallow((state) => ({
      initialize: state.initialize,
      dispose: state.dispose,
    })),
  );

  const userPreferencesAreInitialized = useUserPreferencesStore(
    ({ isInitialized }) => isInitialized,
  );

  const isCanvasInitialized = useRef(false);

  useEffect(() => {
    if (!userPreferencesAreInitialized) return;
    if (isCanvasInitialized.current) return;
    if (!elementRef.current) return;

    const canvas = THiveCanvas.createFromElement(elementRef.current, {
      controllers: {
        elementController: {
          enableSnapping: true,
          snappingThreshold: getUserPreference(UserPreferenceName.CANVAS_SNAPPING_THRESHOLD),
        },
      },
      engine: {
        showStats: getUserPreference(UserPreferenceName.CANVAS_SHOW_FPS),
      },
    });

    // download asset catalog
    const downloadAssetCatalog = (catalogUrl: string) =>
      fetch(catalogUrl)
        .then((res) => res.json())
        .then((catalog) => catalog);

    downloadAssetCatalog('/ft-asset/asset_catalog.json')
      .then((catalog) => {
        canvas.setupAssetCatalog(catalog, '/ft-asset');
      })
      .catch(() => logger.error('Failed to load catalog'));

    initialize(canvas);
    isCanvasInitialized.current = true;

    return () => {
      // Only dispose the canvas if we are not navigating to the visualization module
      if (appSvc.getActiveModule() !== 'visualization') {
        dispose();
        isCanvasInitialized.current = false;
      }
    };
  }, [appSvc, dispose, elementRef, initialize, userPreferencesAreInitialized]);
};
